import React, { useEffect, useMemo, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import PropTypes from 'prop-types'
import { IconButton } from '@material-ui/core'
import { useParams } from 'react-router-dom'
import SydLabel, { hookFormErrorAdapter } from '../../../../commonDesign/SydLabel'
import SydInput from '../../../../commonDesign/SydInput'
import SydSwitch from '../../../../commonDesign/SydSwitch'
import SydSelect from '../../../../commonDesign/SydSelect'
import Icon from '../../../../atoms/Icon'
import { useListAbundanceEngineViews, useListEnablementCodes } from '../../../../../api/abundanceEngine'
import SydAutocomplete from '../../../../commonDesign/SydAutocomplete'
import SydLinkButton from '../../../../commonDesign/LinkButton'
import VersionSelect from './VersionSelect'
import ManageVersionsModal from './ManageVersionsModal'
import ViewTagsPicker from './ViewTagsPicker'

function ViewDetailInputs ({ onAlert }) {
  const { control, formState, watch } = useFormContext()
  const viewId = watch('viewId')
  const [manageModalOpen, setManageModalOpen] = useState(false)

  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')

  const params = useParams()
  const isEdit = useMemo(() => !!params.path?.length, [params])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500)
    return () => clearTimeout(timer)
  }, [searchTerm, debouncedSearchTerm])

  const { data, isLoading } = useListEnablementCodes()
  const { data: views = [], isLoading: isLoadingViews, isFetching: isFetchingViews } = useListAbundanceEngineViews(
    {
      includes: { contents: false },
      page: 1,
      limit: 10,
      sort: [{ field: 'name', dir: 'asc' }],
      search: debouncedSearchTerm?.length ? debouncedSearchTerm : undefined
    },
    {
      mapper: data => data.rows ?? []
    }
  )

  return (
    <div className='flex flex-col gap-5 pb-5'>
      {isEdit && (
        <SydLinkButton
          style={{ margin: 2 }}
          to={`/views/${params.path}`}
          target='_blank'
          icon='visibility'
          size='sm'
        >
          Go to Live View
        </SydLinkButton>
      )}

      <Controller
        name='path'
        control={control}
        rules={{ required: 'Path is required' }}
        render={({ field, fieldState }) => (
          <SydLabel
            label='Path'
            description='The unique path for this view, used in the URL.'
            error={hookFormErrorAdapter({ formState }, fieldState)}
          >
            <SydInput
              placeholder='unique-view-path'
              size='sm'
              required
              {...field}
            />
          </SydLabel>
        )}
      />

      <Controller
        name='name'
        control={control}
        rules={{ required: 'Name is required' }}
        render={({ field, fieldState }) => (
          <SydLabel
            label='Name'
            description='The name of the view, displayed in the navigation and title. (if not hidden)'
            error={hookFormErrorAdapter({ formState }, fieldState)}
          >
            <SydInput
              placeholder='Enter a name for this view...'
              size='sm'
              required
              {...field}
            />
          </SydLabel>
        )}
      />

      <Controller
        name='description'
        control={control}
        render={({ field, fieldState }) => (
          <SydLabel
            label='Description'
            description='A short description of the view.'
            error={hookFormErrorAdapter({ formState }, fieldState)}
          >
            <SydInput
              placeholder='Enter a description for this view...'
              size='sm'
              {...field}
            />
          </SydLabel>
        )}
      />

      <Controller
        name='viewTagIds'
        control={control}
        render={({ field, fieldState }) => (
          <SydLabel
            label='Tags'
            description='Tags used for view discovery and organization'
            error={hookFormErrorAdapter({ formState }, fieldState)}
          >
            <ViewTagsPicker
              value={field.value || []}
              onChange={field.onChange}
            />
          </SydLabel>
        )}
      />

      {isEdit && viewId && (
        <Controller
          name='liveViewVersionId'
          control={control}
          render={({ field }) => (
            <div className='flex items-center gap-sm'>
              <div style={{ flex: 1, minWidth: 0 }}>
                <VersionSelect
                  viewId={viewId}
                  label='Live Version'
                  value={field.value}
                  onChange={(val) => field.onChange(val)}
                  placeholder='Select live version'
                />
              </div>
              <IconButton
                size='small'
                onClick={() => setManageModalOpen(true)}
                title='Manage Versions'
                style={{ marginTop: '20px' }}
              >
                <Icon name='settings' customSize='18px' />
              </IconButton>
            </div>
          )}
        />
      )}

      <Controller
        name='isLive'
        control={control}
        render={({ field, fieldState }) => (
          <SydLabel
            label='Is Live?'
            description='Whether this view is live / accessible to end users.'
            error={hookFormErrorAdapter({ formState }, fieldState)}
          >
            <SydSwitch {...field} />
          </SydLabel>
        )}
      />

      <Controller
        name='showInNavigation'
        control={control}
        render={({ field, fieldState }) => (
          <SydLabel
            label='Show in Navigation?'
            description='Whether to show this view in the left sidebar menu.'
            error={hookFormErrorAdapter({ formState }, fieldState)}
          >
            <SydSwitch {...field} />
          </SydLabel>
        )}
      />

      <Controller
        name='hideTitle'
        control={control}
        render={({ field, fieldState }) => (
          <SydLabel
            label='Hide Title?'
            description='Whether to hide the title of the view when displayed to end users.'
            error={hookFormErrorAdapter({ formState }, fieldState)}

          >
            <SydSwitch {...field} />
          </SydLabel>
        )}
      />

      <Controller
        name='enablementCodeId'
        control={control}
        render={({ field, fieldState }) => (
          <SydLabel
            label='Enablement Code'
            description='The code required to enable this view.'
            error={hookFormErrorAdapter({ formState }, fieldState)}
          >
            <SydSelect
              placeholder={isLoading ? 'Loading enablement codes...' : 'Select an enablement code'}
              options={[
                ...(data?.enablementCodes || []).map(ec => ({
                  label: ec.enablementCode,
                  value: ec.enablementCodeId
                })),
                { label: '<Not Assigned>', value: null }
              ]}
              isClearable
              isLoading={isLoading}
              minWidth='0'
              {...field}
            />
          </SydLabel>
        )}
      />

      <Controller
        name='disabledViewId'
        control={control}
        render={({ field, fieldState }) => (
          <SydLabel
            label='Fallback View (when disabled)'
            description='The view to show when this view is disabled. (if any)'
            error={hookFormErrorAdapter({ formState }, fieldState)}
          >
            <SydAutocomplete
              placeholder={isLoadingViews || isFetchingViews ? 'Loading views...' : 'Select a fallback view'}
              options={(views || []).map(v => ({
                label: v.name,
                value: v.viewId,
                subLabel: v.path
              }))}
              isClearable
              isLoading={isLoadingViews || isFetchingViews}
              onInputChange={(e) => setSearchTerm(e.target.value)}
              {...field}
            />
          </SydLabel>
        )}
      />

      <ManageVersionsModal
        open={manageModalOpen}
        onClose={() => setManageModalOpen(false)}
        viewId={viewId}
        onAlert={onAlert}
      />
    </div>
  )
}

ViewDetailInputs.propTypes = {
  onAlert: PropTypes.func
}

export default ViewDetailInputs
